import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Trophy, Target, Zap, Star, Sparkles } from 'lucide-react';
import { Milestone, Goal } from '@/hooks/useGoals';
import confetti from 'canvas-confetti';

interface MilestoneCelebrationProps {
  milestone: Milestone | null;
  goal: Goal | null;
  isOpen: boolean;
  onClose: () => void;
}

export const MilestoneCelebration = ({ milestone, goal, isOpen, onClose }: MilestoneCelebrationProps) => {
  const { t } = useTranslation();
  const [showConfetti, setShowConfetti] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const formatTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    const secs = Math.round((minutes % 1) * 60);
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    // Cleanup function to stop confetti when dialog closes or component unmounts
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // Reset confetti canvas
      confetti.reset();
    };
  }, []);

  useEffect(() => {
    // Reset confetti when dialog closes
    if (!isOpen) {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      confetti.reset();
      setShowConfetti(false);
      return;
    }

    if (isOpen && milestone && milestone.milestone_type === 'goal_reached') {
      setShowConfetti(true);
      // Trigger confetti animation
      const duration = 3000;
      const animationEnd = Date.now() + duration;

      const frame = () => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          confetti.reset();
          setShowConfetti(false);
          return;
        }

        confetti({
          particleCount: 3,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ['#f5b52a', '#FF6B6B', '#1FD4D4', '#45B7D1', '#96CEB4', '#FFEAA7']
        });
        confetti({
          particleCount: 3,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ['#f5b52a', '#FF6B6B', '#1FD4D4', '#45B7D1', '#96CEB4', '#FFEAA7']
        });

        animationFrameRef.current = requestAnimationFrame(frame);
      };

      animationFrameRef.current = requestAnimationFrame(frame);

      // Stop confetti after 3 seconds
      timeoutRef.current = setTimeout(() => {
        confetti.reset();
        setShowConfetti(false);
        if (animationFrameRef.current !== null) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
      }, duration);
    }
  }, [isOpen, milestone]);

  const getMilestoneIcon = () => {
    if (!milestone) return <Trophy className="h-16 w-16 text-gold" />;
    
    switch (milestone.milestone_type) {
      case 'goal_reached':
        return <Trophy className="h-16 w-16 text-gold" />;
      case 'percentage_milestone':
        return <Target className="h-16 w-16 text-primary" />;
      case 'personal_best':
        return <Zap className="h-16 w-16 text-gold" />;
      default:
        return <Star className="h-16 w-16 text-gold" />;
    }
  };

  const getMilestoneTitle = () => {
    if (!milestone || !goal) return t('components.milestoneCelebration.congratulations');
    
    switch (milestone.milestone_type) {
      case 'goal_reached':
        return t('components.milestoneCelebration.goalAchieved');
      case 'percentage_milestone':
        return t('components.milestoneCelebration.percentComplete', { percent: milestone.percentage_achieved });
      case 'personal_best':
        return t('components.milestoneCelebration.newPersonalBest');
      default:
        return t('components.milestoneCelebration.milestoneReached');
    }
  };

  const getMilestoneMessage = () => {
    if (!milestone || !goal) return '';
    
    const exerciseName = goal.exercise_name || t('components.milestoneCelebration.yourGoal');
    const value = milestone.achievement_value;
    const unit = milestone.achievement_unit || goal.target_unit;
    
    const isRunning = goal.goal_type === 'running';
    const targetTime = isRunning ? parseFloat(goal.description || '0') : goal.target_value;
    
    const formatValue = (val: number) => isRunning ? formatTime(val) : val;
    const formatTarget = (val: number) => isRunning ? formatTime(val) : val;
    const displayUnit = isRunning ? '' : unit;
    const distanceInfo = isRunning ? ` (${goal.target_value}km)` : '';
    
    switch (milestone.milestone_type) {
      case 'goal_reached':
        return t('components.milestoneCelebration.messages.goalReached', {
          exercise: exerciseName,
          target: formatTarget(targetTime),
          unit: displayUnit,
          distanceInfo,
        });
      case 'percentage_milestone':
        return t('components.milestoneCelebration.messages.percentageMilestone', {
          percent: milestone.percentage_achieved,
          exercise: exerciseName,
          value: formatValue(value),
          unit: displayUnit,
          target: formatTarget(targetTime),
          distanceInfo,
        });
      case 'personal_best':
        return t('components.milestoneCelebration.messages.personalBest', {
          exercise: exerciseName,
          value: formatValue(value),
          unit: displayUnit,
          target: formatTarget(targetTime),
          distanceInfo,
        });
      default:
        return t('components.milestoneCelebration.messages.default', { exercise: exerciseName });
    }
  };

  const getArnoldMotivation = () => {
    if (!milestone) return '';
    const idx = Math.floor(Math.random() * 7) + 1;
    return t(`components.milestoneCelebration.quotes.${idx}`);
  };

  if (!milestone || !goal) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogTitle className="sr-only">{t('components.milestoneCelebration.accessibilityTitle')}</DialogTitle>
        <div className="text-center space-y-6 py-6">
          {/* Animated Icon */}
          <div className="relative mx-auto">
            <div className="animate-bounce">
              {getMilestoneIcon()}
            </div>
            <div className="absolute -top-2 -right-2 animate-spin">
              <Sparkles className="h-6 w-6 text-yellow-400" />
            </div>
          </div>

          {/* Title */}
          <div className="space-y-2">
            <h2 className="text-2xl font-bold text-primary animate-pulse">
              {getMilestoneTitle()}
            </h2>
            <div className="text-lg font-semibold">
              {goal.exercise_name}: {goal.goal_type === 'running' 
                ? formatTime(milestone.achievement_value)
                : `${milestone.achievement_value} ${milestone.achievement_unit}`
              }
            </div>
          </div>

          {/* Message */}
          <div className="space-y-4 text-sm text-muted-foreground">
            <p className="leading-relaxed">
              {getMilestoneMessage()}
            </p>
            
            {/* Coach Quote */}
            <div className="bg-gradient-primary/10 p-4 rounded-lg border-l-4 border-primary">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-gradient-primary rounded-full flex items-center justify-center">
                  <span className="text-black text-xs font-bold">✦</span>
                </div>
                <span className="text-sm font-semibold text-primary">{t('components.milestoneCelebration.coachSays')}</span>
              </div>
              <p className="text-sm italic leading-relaxed">
                "{getArnoldMotivation()}"
              </p>
            </div>
          </div>

          {/* Progress Bar for percentage milestones */}
          {milestone.milestone_type === 'percentage_milestone' && (
            <div className="space-y-2">
              <div className="w-full bg-muted rounded-full h-3">
                <div 
                  className="bg-gradient-primary h-3 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${milestone.percentage_achieved}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('components.milestoneCelebration.towardGoal', {
                  percent: milestone.percentage_achieved,
                  target: goal.goal_type === 'running'
                    ? `${formatTime(parseFloat(goal.description || '0'))} (${goal.target_value}km)`
                    : `${goal.target_value} ${goal.target_unit}`,
                })}
              </p>
            </div>
          )}

          {/* Action Button */}
          <Button 
            onClick={onClose} 
            className="w-full bg-gradient-primary text-black hover:shadow-lg transition-all duration-200"
          >
            {t('components.milestoneCelebration.continueTraining')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};